import type { V2Answers, V2Condition, V2ElementValue } from '../../types/V2Types';

/**
 * Pure evaluator for HRR `showIf` conditions. Used in two places:
 *  - page-level (`V2Page.showIf`) to decide if an entire page is rendered
 *  - element-level (`V2Element.showIf`) to hide/show individual elements
 *
 * `value` may be a scalar (eq/neq/gt/...) or an array (in/notIn). For
 * `isEmpty` / `isNotEmpty` the `value` is ignored. Unknown ops return false
 * so a malformed BE payload never crashes the renderer.
 */
export function evaluateCondition(condition: V2Condition, answers: V2Answers): boolean {
	const current = answers[condition.elementId];
	switch (condition.op) {
		case 'eq':
			return looseEq(current, condition.value);
		case 'neq':
			return !looseEq(current, condition.value);
		case 'in':
			return Array.isArray(condition.value) && condition.value.some((v) => looseEq(current, v));
		case 'notIn':
			return Array.isArray(condition.value) && !condition.value.some((v) => looseEq(current, v));
		case 'gt':
			return toNumber(current) > toNumber(condition.value);
		case 'gte':
			return toNumber(current) >= toNumber(condition.value);
		case 'lt':
			return toNumber(current) < toNumber(condition.value);
		case 'lte':
			return toNumber(current) <= toNumber(condition.value);
		case 'contains':
			return Array.isArray(current) && current.some((item) => looseEq(item as V2ElementValue, condition.value));
		case 'isEmpty':
			return isEmpty(current);
		case 'isNotEmpty':
			return !isEmpty(current);
		default:
			return false;
	}
}

/** Convenience: should this page (or element) be shown given the answers? */
export function shouldShow(condition: V2Condition | undefined, answers: V2Answers): boolean {
	if (!condition) return true;
	return evaluateCondition(condition, answers);
}

function looseEq(a: V2ElementValue | undefined, b: V2Condition['value']): boolean {
	if (a === undefined || a === null) return b === undefined || b === null;
	if (typeof a === 'number' && typeof b === 'string') return a === Number(b);
	if (typeof a === 'string' && typeof b === 'number') return Number(a) === b;
	return a === b;
}

function toNumber(value: unknown): number {
	if (typeof value === 'number') return value;
	if (typeof value === 'string' && value.trim() !== '') {
		const n = Number(value);
		return Number.isFinite(n) ? n : Number.NaN;
	}
	return Number.NaN;
}

function isEmpty(value: V2ElementValue | undefined): boolean {
	if (value === undefined || value === null) return true;
	if (typeof value === 'string') return value.trim().length === 0;
	if (Array.isArray(value)) return value.length === 0;
	return false;
}
